import logger from 'lib/logger';
import { ContentModule } from 'components/content/models/ContentModule';
import { RuleResource } from 'components/rules/rules.types';

enum RulesReducerActionType {
    START_REQUEST = 'START_REQUEST',
    LOAD_SUCCESS = 'LOAD_SUCCESS',
    LOAD_ERROR = 'LOAD_ERROR',
    MODAL_ACTION_CREATE = 'MODAL_ACTION_CREATE',
    CREATE_SUCCESS = 'CREATE_SUCCESS',
    MODAL_ACTION_UPDATE = 'MODAL_ACTION_UPDATE',
    UPDATE_SUCCESS = 'UPDATE_SUCCESS',
    MODAL_ACTION_DELETE = 'MODAL_ACTION_DELETE',
    MODAL_ACTION_PAUSE = 'MODAL_ACTION_PAUSE',
    DELETE_SUCCESS = 'DELETE_SUCCESS',
    UNKNOWN = 'UNKNOWN'
}

interface IState {
    isLoading: boolean;
    modules: ContentModule[];
    selectedRule: RuleResource | null;
    actionedRule: { id: string; status: 'created' | 'updated' | 'deleted' } | null;
    createRuleModalOpen: boolean;
    updateRuleModalOpen: boolean;
    deleteRuleModalOpen: boolean;
    pauseRuleModalOpen: boolean;
}
type IReducerAction = IUnknownAction | IProgressAction | ILoadingAction | IModalAction | IModalSuccessAction;
interface IBaseReducerAction {
    type: RulesReducerActionType;
}
interface IUnknownAction extends IBaseReducerAction {
    type: RulesReducerActionType.UNKNOWN;
}
interface IProgressAction extends IBaseReducerAction {
    type: RulesReducerActionType.START_REQUEST;
}
interface ILoadingAction extends IBaseReducerAction {
    type: RulesReducerActionType.LOAD_SUCCESS | RulesReducerActionType.LOAD_ERROR;
    locations?: Location[];
    modules?: ContentModule[];
}
interface IModalAction extends IBaseReducerAction {
    type:
        | RulesReducerActionType.MODAL_ACTION_CREATE
        | RulesReducerActionType.MODAL_ACTION_UPDATE
        | RulesReducerActionType.MODAL_ACTION_DELETE
        | RulesReducerActionType.MODAL_ACTION_PAUSE;
    isOpen: boolean;
    selectedRule?: RuleResource;
}
interface IModalSuccessAction extends IBaseReducerAction {
    type:
        | RulesReducerActionType.CREATE_SUCCESS
        | RulesReducerActionType.UPDATE_SUCCESS
        | RulesReducerActionType.DELETE_SUCCESS;
    rule: RuleResource;
}

const initialState: IState = {
    isLoading: true,
    modules: [],
    selectedRule: null,
    actionedRule: null,
    createRuleModalOpen: false,
    updateRuleModalOpen: false,
    deleteRuleModalOpen: false,
    pauseRuleModalOpen: false
};

function reducer(state: IState, action: IReducerAction): IState {
    switch (action.type) {
        case RulesReducerActionType.START_REQUEST:
            return { ...state, isLoading: true };
        case RulesReducerActionType.LOAD_SUCCESS: {
            const dataToMerge: Partial<IState> = {
                isLoading: false
            };
            if (action.modules) {
                dataToMerge.modules = action.modules;
            }
            return {
                ...state,
                ...dataToMerge
            };
        }
        case RulesReducerActionType.LOAD_ERROR:
            return {
                ...state,
                isLoading: false,
                modules: []
            };
        case RulesReducerActionType.MODAL_ACTION_CREATE:
            return {
                ...state,
                isLoading: false,
                createRuleModalOpen: action.isOpen,
                selectedRule: action.selectedRule
            };
        case RulesReducerActionType.MODAL_ACTION_UPDATE:
            return {
                ...state,
                isLoading: false,
                updateRuleModalOpen: action.isOpen,
                selectedRule: action.selectedRule
            };
        case RulesReducerActionType.MODAL_ACTION_DELETE:
            return {
                ...state,
                isLoading: false,
                deleteRuleModalOpen: action.isOpen,
                selectedRule: action.selectedRule
            };
        case RulesReducerActionType.MODAL_ACTION_PAUSE:
            return {
                ...state,
                isLoading: false,
                pauseRuleModalOpen: action.isOpen,
                selectedRule: action.selectedRule
            };
        case RulesReducerActionType.CREATE_SUCCESS:
            return {
                ...state,
                isLoading: false,
                createRuleModalOpen: false,
                actionedRule: { id: action.rule._id, status: 'created' },
                selectedRule: null
            };
        case RulesReducerActionType.UPDATE_SUCCESS:
            return {
                ...state,
                isLoading: false,
                updateRuleModalOpen: false,
                actionedRule: { id: action.rule._id, status: 'updated' },
                selectedRule: null
            };
        case RulesReducerActionType.DELETE_SUCCESS:
            return {
                ...state,
                isLoading: false,
                deleteRuleModalOpen: false,
                actionedRule: { id: action.rule._id, status: 'deleted' },
                selectedRule: null
            };
        default:
            logger.warn(
                `Expected reducer action type, but got: ${JSON.stringify(action.type)}. RulesPage.tsx-reducer`
            );
            return state;
    }
}

export default { reducer, initialState, ActionType: RulesReducerActionType };
